import React from 'react';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from './button';

/**
 * PlanGateBanner — shows when an API returns 403 with a plan-gate payload.
 *
 * Iter 59c — replaces silent "Loading..." forever on Growth-tier features
 * (ICP Learning, Brain Insights, Command Center) for Starter-plan users.
 *
 * Usage:
 *   const [gate, setGate] = useState(null);
 *   try { ... } catch (e) {
 *     if (e?.response?.status === 403 && e.response.data?.detail?.required_plan)
 *       setGate(e.response.data.detail);
 *   }
 *   return gate ? <PlanGateBanner gate={gate} /> : ...;
 */
export const PlanGateBanner = ({ gate, title, featureIcon: FeatureIcon = Sparkles }) => {
  if (!gate) return null;
  const currentPlan = gate.current_plan || 'starter';
  const requiredPlan = gate.required_plan || 'growth';
  const upgradeUrl = gate.upgrade_url || '/billing';
  const message = gate.message || `This feature requires the ${requiredPlan} plan or above.`;

  return (
    <div
      className="rounded-2xl border border-violet-500/25 bg-gradient-to-br from-violet-500/[0.08] via-violet-500/[0.04] to-transparent p-8 max-w-3xl mx-auto"
      data-testid="plan-gate-banner"
    >
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-xl bg-violet-500/15 border border-violet-500/20 flex items-center justify-center shrink-0">
          <Lock className="h-6 w-6 text-violet-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <FeatureIcon className="h-4 w-4 text-violet-500" />
            <span className="text-xs font-semibold uppercase tracking-wider text-violet-500">
              {requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)} plan feature
            </span>
          </div>
          <h3 className="text-lg font-semibold mb-1.5">
            {title || `Unlock with the ${requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)} plan`}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            {message}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={() => (window.location.href = upgradeUrl)}
              className="bg-violet-500 hover:bg-violet-600 text-white gap-2"
              data-testid="plan-gate-upgrade-btn"
            >
              Upgrade to {requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)}
              <ArrowRight className="h-4 w-4" />
            </Button>
            <span className="text-xs text-muted-foreground">
              You're on the <span className="font-medium">{currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}</span> plan
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanGateBanner;
